import React, { useEffect, useState } from 'react';
import { Product } from '../../types';
import { useCrudList } from '../../hooks/useCrudList';
import { MessageSetter } from '../../utils/messages';
import { Icons } from '../../utils/icons';

interface ManageProjectsProps {
  userRole: string;
  setMessage: MessageSetter;
}

const ManageProjectsSection: React.FC<ManageProjectsProps> = ({
  userRole,
  setMessage,
}) => {
  const {
    items: products,
    loading,
    fetchAll: fetchProducts,
    create: createProduct,
    update: updateProduct,
    remove: removeProduct,
  } = useCrudList<Product>({
    endpoint: '/api/products',
    useAuth: true,
    setMessage,
  });

  const [editingProduct, setEditingProduct] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{ name: string; projectId: string }>({
    name: '',
    projectId: '',
  });

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const validateProjectId = (projectId: string): boolean => {
    if (projectId && !/^[A-Z0-9]{2,10}$/.test(projectId)) {
      setMessage({
        type: 'error',
        text: 'Project ID must be 2-10 uppercase alphanumeric characters (e.g., FS, ATAK)',
      });
      return false;
    }
    return true;
  };

  const handleSaveProduct = async (productId: string) => {
    if (!editForm.name.trim()) {
      setMessage({ type: 'error', text: 'Product name is required' });
      return;
    }

    if (!validateProjectId(editForm.projectId)) return;

    try {
      await updateProduct(productId, {
        name: editForm.name.trim(),
        projectId: editForm.projectId.trim().toUpperCase() || undefined,
      }, 'Product updated successfully');
      setEditingProduct(null);
    } catch (error) {
      console.error('Error updating product:', error);
    }
  };

  const handleCreateProduct = async () => {
    if (!editForm.name.trim()) {
      setMessage({ type: 'error', text: 'Project name is required' });
      return;
    }
    if (!editForm.projectId.trim() || editForm.projectId.length < 2) {
      setMessage({
        type: 'error',
        text: 'Project ID is required (minimum 2 characters)',
      });
      return;
    }

    try {
      await createProduct({
        name: editForm.name.trim(),
        projectId: editForm.projectId.trim(),
      }, 'Project created successfully');
      setEditingProduct(null);
      setEditForm({ name: '', projectId: '' });
    } catch (error) {
      console.error('Error creating project:', error);
    }
  };

  const handleDeleteProduct = async (product: Product) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${product.name}"? This will also delete all associated features and sessions.`,
      )
    ) {
      return;
    }

    try {
      await removeProduct(product.id, 'Product deleted successfully');
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingProduct(null);
    setEditForm({ name: '', projectId: '' });
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product.id);
    setEditForm({
      name: product.name,
      projectId: product.projectId || '',
    });
  };

  if (loading) {
    return (
      <div className="card">
        <p>Loading projects...</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-lg">
        <h2 className="title-lg">Manage Projects</h2>
        {(userRole === 'admin' || userRole === 'manager') && editingProduct !== 'new' && (
          <button
            className="btn btn-primary"
            onClick={() => {
              setEditingProduct('new');
              setEditForm({ name: '', projectId: '' });
            }}
          >
            ➕ Add Project
          </button>
        )}
      </div>
      <p
        style={{
          color: 'var(--text-secondary)',
          marginBottom: '1.5rem',
          fontSize: '0.9rem',
        }}
      >
        {userRole === 'admin' || userRole === 'manager'
          ? 'Create and edit project names and IDs. The Project ID is used as a prefix for session IDs (e.g., "FS" creates sessions like "FS-1", "FS-2").'
          : 'View project names and IDs. The Project ID is used as a prefix for session IDs (e.g., "FS" creates sessions like "FS-1", "FS-2").'}
      </p>

      {/* New Project Form */}
      {editingProduct === 'new' && (
        <div
          className="card mb-lg"
          style={{
            backgroundColor: 'var(--surface-alt)',
            border: '2px solid var(--primary)',
          }}
        >
          <h3 className="title-md mb-md">Create New Project</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label className="label">Project Name *</label>
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="input"
                placeholder="e.g., My Application"
              />
            </div>
            <div>
              <label className="label">Project ID *</label>
              <input
                type="text"
                value={editForm.projectId}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    projectId: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''),
                  })
                }
                className="input"
                style={{ textTransform: 'uppercase' }}
                placeholder="e.g., FS, ATAK, APP"
                maxLength={10}
              />
              <p className="hint mt-sm">
                2-10 uppercase letters/numbers. Used as prefix for session IDs (e.g., "FS" →
                "FS-1", "FS-2")
              </p>
            </div>
            <div className="flex gap-sm">
              <button className="btn btn-primary" onClick={handleCreateProduct}>
                Create Project
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setEditingProduct(null);
                  setEditForm({ name: '', projectId: '' });
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {products.length === 0 && editingProduct !== 'new' ? (
        <p style={{ color: 'var(--text-muted)', padding: '2rem', textAlign: 'center' }}>
          No projects found. Click &quot;Add Project&quot; to create one.
        </p>
      ) : (
        products.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {products.map((product) => (
              <div
                key={product.id}
                style={{
                  padding: '1.25rem',
                  border:
                    editingProduct === product.id
                      ? '2px solid var(--cyan-glow)'
                      : '1px solid var(--grid-line)',
                  borderRadius: '8px',
                  backgroundColor:
                    editingProduct === product.id ? 'var(--surface-alt)' : 'var(--surface)',
                  transition: 'all 0.2s',
                }}
              >
                {editingProduct === product.id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                      <label
                        style={{
                          display: 'block',
                          marginBottom: '0.5rem',
                          fontWeight: 600,
                          color: 'var(--text-primary)',
                        }}
                      >
                        Product Name
                      </label>
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            name: e.target.value,
                          })
                        }
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '1rem',
                        }}
                        placeholder="Product name"
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: 'block',
                          marginBottom: '0.5rem',
                          fontWeight: 600,
                          color: 'var(--text-primary)',
                        }}
                      >
                        Project ID
                      </label>
                      <input
                        type="text"
                        value={editForm.projectId}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            projectId: e.target.value.toUpperCase(),
                          })
                        }
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '1rem',
                          textTransform: 'uppercase',
                        }}
                        placeholder="FS, ATAK, etc. (2-10 uppercase alphanumeric)"
                        maxLength={10}
                      />
                      <p
                        style={{
                          fontSize: '0.75rem',
                          color: 'var(--text-secondary)',
                          marginTop: '0.25rem',
                        }}
                      >
                        Used as prefix for session IDs (e.g., &quot;FS&quot; → &quot;FS-1&quot;,
                        &quot;FS-2&quot;)
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      <button
                        className="btn btn-primary"
                        onClick={() => handleSaveProduct(product.id)}
                        style={{ padding: '0.75rem 1.5rem' }}
                      >
                        Save
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={handleCancelEdit}
                        style={{ padding: '0.75rem 1.5rem' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          color: 'var(--text-primary)',
                          marginBottom: '0.25rem',
                        }}
                      >
                        {product.name}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        Project ID:{' '}
                        {product.projectId || (
                          <span
                            style={{
                              fontStyle: 'italic',
                              color: 'var(--text-muted)',
                            }}
                          >
                            Not set
                          </span>
                        )}
                        {product.projectId && (
                          <span style={{ marginLeft: '0.5rem', color: 'var(--cyan-glow)' }}>
                            (Sessions: {product.projectId}-1, {product.projectId}-2, ...)
                          </span>
                        )}
                      </div>
                    </div>
                    {(userRole === 'admin' || userRole === 'manager') && (
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          className="btn btn-primary"
                          onClick={() => handleEditProduct(product)}
                          style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                        >
                          ✏️ Edit
                        </button>
                        {userRole === 'admin' && (
                          <button
                            className="btn btn-secondary"
                            onClick={() => handleDeleteProduct(product)}
                            style={{
                              padding: '0.5rem 1rem',
                              fontSize: '0.875rem',
                              backgroundColor: '#e74c3c',
                              color: 'white',
                            }}
                          >
                            {Icons.delete} Delete
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default ManageProjectsSection;
